/**
 * This utility file provides functions to process and transform raw materialized table data into a structured format
 * suitable for use in the table definition editor. It handles:
 * - Mapping raw table rows and headers to processed cells with additional metadata (e.g., overrides).
 * - Filtering out unnecessary property and unit-of-measure (UOM) columns from the processed data.
 * - Processing individual cell types (e.g., numeric, UUID, boolean) based on their column definitions.
 * - Adding override indicators to cells when applicable.
 */
import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { sqScalingTableDefinitionStore } from '@/core/core.stores';
import {
  PROPERTY_COLUMN_MATCH_SEGMENT,
  UOM_COLUMN_MATCH_SUFFIX,
  parseDisplayNameColumnName,
} from '@/tableDefinitionEditor/columnMatch.constants';
import {
  filterPropertyAndUomColumns,
  getHeaderName,
} from '@/tableDefinitionEditor/materializedTableColumnFilter.utilities';
import {
  isPublicationProvenanceProjectionHeader,
  parsePublicationProvenance,
} from '@/tableDefinitionEditor/publicationProvenance.utilities';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import { CSV_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { toNumber } from '@/utilities/numberHelper.utilities';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type {
  BooleanTableCell,
  ColumnDefinition,
  EditorType,
  ItemTableCell,
  MaterializedTableCell,
  MaterializedTableHeader,
  MaterializedTableOutput,
  MaterializedTableRow,
  NumericTableCell,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  TextTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';

/** Fast lookup for header name -> column index. */
type HeaderIndexMap = Map<string, number>;
/** Fast lookup for column name -> column definition. */
type ColumnByNameMap = Map<string, ColumnDefinition>;
/** Fast lookup for column id -> override column index. */
type OverrideIndexMap = Map<string, number>;
/** Property name plus optional header index for UUID property columns. */
type PropertyHeaderIndex = { property: string; index?: number };
/** Fast lookup for UUID column name -> property header indices. */
type PropertyIndexMap = Map<string, PropertyHeaderIndex[]>;
/** Fast lookup for UUID column name -> `{uuid}.display.Name` header index. */
type DisplayNameHeaderIndexMap = Map<string, number>;

/** Processing instructions aligned to each header index. */
type HeaderProcessingPlan = {
  header: MaterializedTableHeader;
  column?: ColumnDefinition;
  overrideIndex?: number;
  uomIndex?: number;
  propertyIndices?: PropertyHeaderIndex[];
  displayNameHeaderIndex?: number;
};

/** Shared lookup context used to process every row. */
type ProcessingContext = {
  publicationProvenanceIndex?: number;
  processingPlans: HeaderProcessingPlan[];
};

/** Build a name -> index map for headers. */
const buildHeaderIndexMap = (headers: MaterializedTableHeader[]): HeaderIndexMap => {
  return headers.reduce((map, header, index) => {
    const headerName = getHeaderName(header);
    if (!headerName) {
      return map;
    }
    map.set(headerName, index);
    return map;
  }, new Map<string, number>());
};

/** Build a columnName -> columnDefinition map for O(1) access. */
const buildColumnByNameMap = (columns: ColumnDefinition[]): ColumnByNameMap => {
  return columns.reduce((map, column) => {
    map.set(column.columnName, column);
    return map;
  }, new Map<string, ColumnDefinition>());
};

/**
 * Extract the base column id from an override-table header name.
 * Supports legacy qualified aliases (e.g. `table."{columnId}_overrides"`) and identity aliases
 * from the materialized-table query builder (e.g. `{columnId}_overrides`).
 */
export const extractOverrideColumnIdFromHeader = (headerName: string): string | undefined => {
  const legacyQualifiedMatch = headerName.match(/\."([^"]+)_overrides"$/);
  if (legacyQualifiedMatch?.[1]) {
    return legacyQualifiedMatch[1].toLowerCase();
  }

  const identityMatch = headerName.match(/^(.+)_overrides$/);
  if (identityMatch?.[1] && !identityMatch[1].includes('.')) {
    return identityMatch[1].toLowerCase();
  }

  return undefined;
};

/** Capture override header indices keyed by column id. */
const buildOverrideIndexMap = (headers: MaterializedTableHeader[]): OverrideIndexMap => {
  return headers.reduce((map, header, index) => {
    const headerName = getHeaderName(header);
    if (!headerName) {
      return map;
    }
    const columnId = extractOverrideColumnIdFromHeader(headerName);
    if (columnId && !map.has(columnId)) {
      map.set(columnId, index);
    }
    return map;
  }, new Map<string, number>());
};

/** Precompute property header indices for UUID columns. */
const buildPropertyIndexMap = (columns: ColumnDefinition[], headerIndexByName: HeaderIndexMap): PropertyIndexMap => {
  return columns.reduce((map, column) => {
    if (column.columnType !== ColumnTypeEnum.UUID) {
      return map;
    }
    const propertySet = new Set<string>([
      ...(column.additionalProperties || []),
      SeeqNames.Properties.IsModifiedOutsideTable,
      SeeqNames.Properties.Type,
      SeeqNames.Properties.Name,
      ...(column.displayProperty ? [column.displayProperty] : []),
    ]);
    const propertyIndices = Array.from(propertySet).map((property) => ({
      property,
      index: headerIndexByName.get(`${column.columnName}${PROPERTY_COLUMN_MATCH_SEGMENT}${property}`),
    }));
    map.set(column.columnName, propertyIndices);
    return map;
  }, new Map<string, PropertyHeaderIndex[]>());
};

/**
 * Map each UUID base column to its `{uuid}.display.Name` header index when the backend emitted the
 * COALESCE(aliases…, Name) field (tablesToJoin present).
 */
const buildDisplayNameHeaderIndexMap = (
  headers: MaterializedTableHeader[],
  headerIndexByName: HeaderIndexMap,
): DisplayNameHeaderIndexMap => {
  const map = new Map<string, number>();
  headers.forEach((header) => {
    const headerName = getHeaderName(header);
    const uuidColumn = parseDisplayNameColumnName(headerName);
    if (!uuidColumn) {
      return;
    }
    const index = headerIndexByName.get(headerName);
    if (index !== undefined) {
      map.set(uuidColumn, index);
    }
  });
  return map;
};

/** Build per-header processing instructions to avoid repeated lookups. */
const buildHeaderProcessingPlans = (
  headers: MaterializedTableHeader[],
  columnsByName: ColumnByNameMap,
  overrideIndexByColumnId: OverrideIndexMap,
  headerIndexByName: HeaderIndexMap,
  propertyIndexByColumnName: PropertyIndexMap,
  displayNameHeaderIndexByColumnName: DisplayNameHeaderIndexMap,
): HeaderProcessingPlan[] => {
  return headers.map((header) => {
    const headerName = getHeaderName(header);
    const column = headerName ? columnsByName.get(headerName) : undefined;
    const overrideIndex = column?.id ? overrideIndexByColumnId.get(column.id.toLowerCase()) : undefined;
    const plan: HeaderProcessingPlan = {
      header,
      column,
      overrideIndex,
    };

    if (header.type === ColumnTypeEnum.NUMERIC) {
      plan.uomIndex = headerName ? headerIndexByName.get(`${headerName}${UOM_COLUMN_MATCH_SUFFIX}`) : undefined;
    }

    if (header.type === ColumnTypeEnum.UUID) {
      plan.propertyIndices = headerName ? (propertyIndexByColumnName.get(headerName) ?? []) : [];
      plan.displayNameHeaderIndex = headerName ? displayNameHeaderIndexByColumnName.get(headerName) : undefined;
    }

    return plan;
  });
};

const overrideIndicator = (row: MaterializedTableRow, overrideIndex: number | undefined) => {
  if (overrideIndex === undefined) {
    return false;
  }
  const overrideValue = row[overrideIndex];
  return overrideValue !== undefined && overrideValue !== null;
};

const processDefaultCell = (cellValue: MaterializedTableCell) => {
  return {
    value: cellValue ? cellValue.toString() : undefined,
  } as TextTableCell;
};

/** Process numeric values with optional UOM lookup. */
const processNumericCell = ({
  cellValue,
  row,
  uomIndex,
  column,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  uomIndex: number | undefined;
  column: ColumnDefinition | undefined;
}) => {
  const potentialNumericValue = toNumber(cellValue);
  const potentialUomColumnValue = uomIndex !== undefined ? row[uomIndex] : undefined;
  // If there was not a uom column, then check the column definition for a uom override
  const uomColumnValue = potentialUomColumnValue ?? column?.columnUom;

  if (_.isNil(potentialNumericValue)) {
    return processDefaultCell(cellValue);
  } else {
    return {
      value: potentialNumericValue,
      uom: uomColumnValue as string | undefined,
    } as Partial<NumericTableCell>;
  }
};

/** Process UUID cells, attaching properties and backend `{uuid}.display.Name` when present. */
const processUuidCell = ({
  cellValue,
  row,
  propertyIndices,
  displayNameHeaderIndex,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  propertyIndices: PropertyHeaderIndex[];
  displayNameHeaderIndex?: number;
}) => {
  const displayNameRaw = displayNameHeaderIndex !== undefined ? row[displayNameHeaderIndex] : undefined;
  const displayNameValue =
    displayNameRaw !== undefined && displayNameRaw !== null && displayNameRaw !== ''
      ? String(displayNameRaw)
      : undefined;

  const fetchedProperties = propertyIndices.reduce(
    (props, { property, index }) => {
      props[property] = index !== undefined ? (row[index] as string) : undefined;
      return props;
    },
    {} as Record<string, string | undefined>,
  );

  // If the value is the same as the table definition id, then it is a not found item from CSV import
  const id =
    cellValue === sqScalingTableDefinitionStore.id
      ? { tableDefinitionId: cellValue as string }
      : { uuid: cellValue as string };

  const joinedMetadata = displayNameValue ? { displayName: displayNameValue } : undefined;

  return {
    ...id,
    fetchedProperties,
    isModifiedOutsideTable: fetchedProperties[SeeqNames.Properties.IsModifiedOutsideTable],
    joinedMetadata,
  } as Partial<ItemTableCell>;
};

/** Process boolean cells with a consistent truthy conversion. */
const processBooleanCell = (cellValue: MaterializedTableCell) => {
  return {
    value: !!cellValue,
  } as Partial<BooleanTableCell>;
};

/** Process a cell based on its header type. */
const processCellByType = (processCellProps: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  plan: HeaderProcessingPlan;
}) => {
  if (_.isNil(processCellProps.cellValue)) {
    return {
      value: undefined,
    };
  }

  switch (processCellProps.plan.header.type) {
    case ColumnTypeEnum.NUMERIC:
      return processNumericCell({
        cellValue: processCellProps.cellValue,
        row: processCellProps.row,
        uomIndex: processCellProps.plan.uomIndex,
        column: processCellProps.plan.column,
      });
    case ColumnTypeEnum.UUID:
      return processUuidCell({
        cellValue: processCellProps.cellValue,
        row: processCellProps.row,
        propertyIndices: processCellProps.plan.propertyIndices ?? [],
        displayNameHeaderIndex: processCellProps.plan.displayNameHeaderIndex,
      });
    case ColumnTypeEnum.BOOLEAN:
      return processBooleanCell(processCellProps.cellValue);
    default: {
      return processDefaultCell(processCellProps.cellValue);
    }
  }
};

/** Convert a single materialized row into processed cells using shared lookups. */
const mapRowToProcessedCells = (row: MaterializedTableRow, context: ProcessingContext): ProcessedTableCell[] => {
  const publicationProvenance =
    context.publicationProvenanceIndex === undefined
      ? undefined
      : parsePublicationProvenance(row[context.publicationProvenanceIndex]);
  const eventOverrideColumnIds = new Set(
    publicationProvenance?.eventOverrideColumnIds.map((columnId) => columnId.toLowerCase()),
  );
  const processedRow: ProcessedTableCell[] = new Array(context.processingPlans.length);
  for (let columnIndex = 0; columnIndex < context.processingPlans.length; columnIndex += 1) {
    const plan = context.processingPlans[columnIndex];
    const cellValue = row[columnIndex];
    const override = overrideIndicator(row, plan.overrideIndex);
    const processedCell: ProcessedTableCell = {
      override,
      ...processCellByType({
        cellValue,
        row,
        plan,
      }),
    };
    if (
      !override &&
      plan.column?.id &&
      publicationProvenance &&
      eventOverrideColumnIds.has(plan.column.id.toLowerCase())
    ) {
      processedCell.publicationProvenance = publicationProvenance.publicationProvenance;
    }
    processedRow[columnIndex] = processedCell;
  }
  return processedRow;
};

/** Build the shared lookup context required to process rows. */
const buildProcessingContext = (headers: MaterializedTableHeader[], columns: ColumnDefinition[]): ProcessingContext => {
  const headerIndexByName = buildHeaderIndexMap(headers);
  const columnsByName = buildColumnByNameMap(columns);
  const overrideIndexByColumnId = buildOverrideIndexMap(headers);
  const propertyIndexByColumnName = buildPropertyIndexMap(columns, headerIndexByName);
  const displayNameHeaderIndexByColumnName = buildDisplayNameHeaderIndexMap(headers, headerIndexByName);
  const processingPlans = buildHeaderProcessingPlans(
    headers,
    columnsByName,
    overrideIndexByColumnId,
    headerIndexByName,
    propertyIndexByColumnName,
    displayNameHeaderIndexByColumnName,
  );
  const publicationProvenanceIndex = headers.findIndex(isPublicationProvenanceProjectionHeader);

  return {
    publicationProvenanceIndex: publicationProvenanceIndex >= 0 ? publicationProvenanceIndex : undefined,
    processingPlans,
  };
};

/** Process a raw materialized table and its display.Name metadata into cells for ag-grid. */
export const processMaterializedTable = (
  rawMaterializedTable: MaterializedTableOutput | undefined,
  columns: ColumnDefinition[],
): ProcessedMaterializedTable | undefined => {
  if (!rawMaterializedTable) {
    return undefined;
  }
  const rows = rawMaterializedTable.rows;
  const headers = rawMaterializedTable.headers;
  const processingContext = buildProcessingContext(headers, columns);

  const processedRows: ProcessedTableCell[][] = rows.map((row) => mapRowToProcessedCells(row, processingContext));

  return filterPropertyAndUomColumns(headers, processedRows, rawMaterializedTable.hasMore);
};

export const getRowInTableByItemId = (itemIdColumnValue: string, editorType: EditorType) => {
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  const materializedTable = tableDefinitionStore.materializedTable;
  const tableType = tableDefinitionStore.tableType;
  const columnHeaderToSearch = CSV_TYPES.includes(tableType)
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  const headerIndex = materializedTable?.headers.findIndex((header) => getHeaderName(header) === columnHeaderToSearch);
  if (headerIndex === undefined || headerIndex < 0) {
    return undefined;
  }

  return materializedTable?.rows?.find((row) => {
    const itemIdColumnCellToCheck = row[headerIndex] as ItemTableCell;
    return itemIdColumnCellToCheck && itemIdColumnCellToCheck.uuid === itemIdColumnValue;
  });
};
